import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { DatePickerInput, TimeInput } from '@mantine/dates'
import { instantToWallClock, wallClockToInstant } from '@racedash/shared'
import type { SessionType } from '@racedash/shared'
import { createSession, updateSession } from '../data/catalog'
import type { Series, Session } from '../data/catalog'

const SESSION_TYPES: SessionType[] = ['practice', 'qualifying', 'race', 'other']

/** The editable fields, in the shape they are compared in. */
interface Draft {
  name: string
  type: SessionType
  seriesId: string | null
  day: string | null
  startTime: string
  minutes: string
}

const EMPTY: Draft = {
  name: '',
  type: 'race',
  seriesId: null,
  day: null,
  startTime: '',
  minutes: '',
}

const FIELDS = Object.keys(EMPTY) as (keyof Draft)[]

function same(a: Draft, b: Draft): boolean {
  return FIELDS.every((k) => a[k] === b[k])
}

/** The stored session, in the form's own terms. */
function draftOf(session: Session, timezone: string): Draft {
  const start = session.scheduledStart?.toDate?.()
  const end = session.scheduledEnd?.toDate?.()
  const local = start ? instantToWallClock(start, timezone) : ''
  return {
    name: session.name,
    type: session.type,
    seriesId: session.seriesId,
    day: local.slice(0, 10) || null,
    startTime: local.slice(11, 16),
    minutes: start && end ? String(Math.round((end.getTime() - start.getTime()) / 60_000)) : '',
  }
}

/**
 * Adding and editing a session.
 *
 * One dialog for both, because they are the same six fields and a separate edit
 * form would be the same code drifting apart. Which it is shows in the title
 * and the button, not in the shape.
 *
 * Entering a weekend means entering a run of sessions, so adding offers to stay
 * open. That is what an inline form gave for free and a dialog otherwise takes
 * away — the day and series carry over, since consecutive sessions nearly
 * always share them.
 *
 * An edit is held against a baseline of what is stored, so Save changes is dead
 * until something has actually changed. Reopening a session to look at it and
 * closing it again should not be able to write anything.
 */
export default function SessionDialog({
  opened,
  onClose,
  eventId,
  timezone,
  firstDay,
  series,
  /** The session being edited, or undefined when adding. */
  session,
}: {
  opened: boolean
  onClose: () => void
  eventId: string
  timezone: string
  /** The event's first day, as YYYY-MM-DD — what a new session starts from. */
  firstDay: string
  series: Series[]
  session?: Session
}) {
  const editing = Boolean(session)

  const [draft, setDraft] = useState<Draft>(EMPTY)
  /** What the session looked like when the dialog opened. */
  const [baseline, setBaseline] = useState<Draft>(EMPTY)
  const [again, setAgain] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  // Reset on open rather than on mount: the dialog stays mounted so it can
  // animate, and an edit must show the session being edited rather than
  // whatever was last typed.
  useEffect(() => {
    if (!opened) return
    setFailed(null)
    if (session) {
      const next = draftOf(session, timezone)
      setDraft(next)
      setBaseline(next)
    } else {
      // Day and series are deliberately carried over between consecutive adds —
      // see the reset in submit(). They fall back to the event's first day only
      // when the dialog has nothing to carry.
      setDraft((d) => ({
        ...EMPTY,
        seriesId: d.seriesId,
        day: d.day ?? (firstDay || null),
      }))
      setBaseline(EMPTY)
    }
  }, [opened, session, timezone, firstDay])

  const length = Number(draft.minutes)
  const startInstant =
    draft.day && draft.startTime
      ? wallClockToInstant(`${draft.day}T${draft.startTime}`, timezone)
      : null
  const endInstant =
    startInstant && Number.isFinite(length) && length > 0
      ? new Date(startInstant.getTime() + length * 60_000)
      : null

  const complete = Boolean(draft.name.trim() && draft.seriesId && startInstant && endInstant)
  const changed = !same(draft, baseline)
  // Adding is gated on being complete; editing also on there being something to
  // save, so a look-and-close cannot write.
  const canSubmit = complete && (!editing || changed)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !canSubmit || !startInstant || !endInstant || !draft.seriesId) return
    setBusy(true)
    setFailed(null)
    try {
      const input = {
        eventId,
        seriesId: draft.seriesId,
        name: draft.name.trim(),
        type: draft.type,
        scheduledStart: startInstant,
        scheduledEnd: endInstant,
      }
      if (session) {
        await updateSession(session.id, input)
        // Saved is the new baseline, so the button falls dead again rather than
        // inviting the same write twice.
        setBaseline(draft)
        onClose()
      } else {
        await createSession(input)
        if (again) {
          // Keep the day and the series; clear what genuinely differs each time.
          setDraft((d) => ({ ...d, name: '', startTime: '', minutes: '' }))
        } else {
          onClose()
        }
      }
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save the session.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => !busy && onClose()}
      title={editing ? `Edit ${session?.name}` : 'New session'}
      size="lg"
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
    >
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}

          <Group grow align="flex-start">
            <TextInput
              label="Session name"
              value={draft.name}
              onChange={(e) => set('name', e.currentTarget.value)}
              placeholder="Race"
              data-autofocus
              required
            />
            <Select
              label="Type"
              data={SESSION_TYPES}
              value={draft.type}
              onChange={(v) => set('type', (v as SessionType) ?? 'race')}
              allowDeselect={false}
            />
          </Group>

          <Select
            label="Series"
            data={series.map((s) => ({ value: s.id, label: s.name }))}
            value={draft.seriesId}
            onChange={(v) => set('seriesId', v)}
            placeholder="Choose…"
            required
          />

          <Group grow align="flex-start">
            <DatePickerInput
              label="Day"
              value={draft.day}
              onChange={(v) => set('day', v)}
              required
            />
            <TimeInput
              label={`Starts (${timezone})`}
              value={draft.startTime}
              onChange={(e) => set('startTime', e.currentTarget.value)}
              required
            />
            <NumberInput
              label="Length (minutes)"
              value={draft.minutes}
              onChange={(v) => set('minutes', String(v))}
              min={1}
              placeholder="45"
              required
            />
          </Group>

          <Text c="dimmed" size="xs">
            {endInstant
              ? `Ends ${instantToWallClock(endInstant, timezone).replace('T', ' ')}.`
              : 'A timetable publishes a length, not an end time — 20 minutes of qualifying, a 4-hour race.'}{' '}
            The end is stored as an instant computed from the start, so a session running through a
            clock change is still the length it was entered as.
          </Text>

          <Group justify="space-between" mt="xs">
            {editing ? (
              <Button
                variant="subtle"
                color="gray"
                type="button"
                onClick={() => setDraft(baseline)}
                disabled={!changed}
              >
                Revert
              </Button>
            ) : (
              <Checkbox
                label="Add another"
                checked={again}
                onChange={(e) => setAgain(e.currentTarget.checked)}
                description="Keeps this open, and keeps the day and series"
              />
            )}
            <Group>
              <Button variant="default" type="button" onClick={onClose} disabled={busy}>
                {editing ? 'Cancel' : 'Done'}
              </Button>
              <Button type="submit" loading={busy} disabled={!canSubmit}>
                {editing ? 'Save changes' : 'Add session'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
